import React, { useEffect, useState } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import type { TutorialStep } from '../process/ResearchWelcomePanel';

// BAR-538: tutorial card for the start page. All four step titles are
// listed on the left; the visual for the selected step shows on the
// right. Selection auto-rolls every 10s (loops), pauses while the
// pointer is over the card, and follows hover/click/focus directly.
// Step content comes from the workspace welcome panels' exported
// arrays, so the tutorial has one source of truth.
const STEP_MS = 10_000;


interface ModuleTutorialCardProps {
    steps: TutorialStep[];
    /** Accent lane: research = navy (primary), build = copper. Mirrors the
     *  step-number chip colors the welcome panels use. */
    variant: 'research' | 'build';
}

// Mounts hidden and fades in (240ms ease-out). Keyed on the step by the
// parent, so every step change starts from the hidden baseline — no
// full-opacity flash frame. Reduced motion renders visible immediately.
function StepVisual({ children, reducedMotion }: { children: React.ReactNode; reducedMotion: boolean }) {
    const [entered, setEntered] = useState(reducedMotion);
    useEffect(() => {
        if (reducedMotion) return;
        const t = setTimeout(() => setEntered(true), 30);
        return () => clearTimeout(t);
    }, [reducedMotion]);
    return (
        <div
            className={`w-full max-w-[480px] transition-all duration-[240ms] ease-out
                ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}
        >
            {children}
        </div>
    );
}

export default function ModuleTutorialCard({ steps, variant }: ModuleTutorialCardProps) {
    const reducedMotion = usePrefersReducedMotion();
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);

    // Slow auto-roll (10s per step, loops). Pointer over the card pauses;
    // reduced-motion users page manually.
    useEffect(() => {
        if (reducedMotion || paused) return;
        const t = setTimeout(() => setActive((n) => (n + 1) % steps.length), STEP_MS);
        return () => clearTimeout(t);
    }, [active, paused, reducedMotion, steps.length]);

    const chipActive = variant === 'build'
        ? 'bg-accent-copper text-white'
        : 'bg-[color:var(--primary)] text-on-primary';
    const chipIdle = variant === 'build'
        ? 'bg-accent-copper-tint text-accent-copper'
        : 'bg-[color:var(--primary)]/10 text-[color:var(--primary)]';

    const step = steps[active];

    return (
        <div
            className="rounded-xl bg-bg-warm shadow-bark-sm px-5 py-5"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)] gap-6 md:items-center">
                {/* Left: all step titles, selected one highlighted */}
                <div className="flex flex-col gap-1" role="tablist" aria-label="Tutorial steps">
                    {steps.map((s, i) => {
                        const isActive = i === active;
                        return (
                            <button
                                key={s.title}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                onClick={() => setActive(i)}
                                onMouseEnter={() => setActive(i)}
                                onFocus={() => setActive(i)}
                                className={`flex items-start gap-3 text-left rounded-md px-2 py-2 transition-colors
                                           ${isActive ? 'bg-surface shadow-bark-xs' : 'hover:bg-surface/60'}`}
                            >
                                <span
                                    className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md
                                               font-mono text-[12px] font-semibold transition-colors
                                               ${isActive ? chipActive : chipIdle}`}
                                    aria-hidden="true"
                                >
                                    {i + 1}
                                </span>
                                <span
                                    className={`text-[13px] leading-[1.4] pt-0.5 transition-colors
                                               ${isActive ? 'font-semibold text-fg' : 'font-medium text-fg-secondary'}`}
                                >
                                    {s.title}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Right: the selected step's visual (compact variant when provided) */}
                <div className="h-[270px] flex items-center justify-center">
                    <StepVisual key={active} reducedMotion={reducedMotion}>
                        {step.carouselMock ?? step.mock}
                    </StepVisual>
                </div>
            </div>
        </div>
    );
}
